import {
  UiPaymentMethodType,
  type UiBankAccountType,
  type UiPaymentMethod,
} from "@/plugins/controls/common/billing-mappers";
import {
  type AddHarnessCustomerPaymentMethodBody,
  type UpdateHarnessCustomerPaymentMethodBody,
} from "@/api/providers/payment-methods"
import { validateBillingAddressFields, type BillingAddressValidationErrors } from "@/plugins/controls/common/billing-address"
import {
  type AchAccountHolderType,
  type AchAccountType,
} from "@/plugins/controls/common/billing-payment-helpers"
import { type CollectJsTokenizedPayment } from "@/plugins/controls/payment-methods-control/payment-methods-drawer/collect-js-payment-form/CollectJsPaymentForm"

export type MethodFormState = {
  type: UiPaymentMethodType
  firstName: string
  lastName: string
  company: string
  address1: string
  address2: string
  city: string
  stateId: number | null
  postalCode: string
  countryId: number | null
  phone: string
  fax: string
  brand: string
  last4: string
  expMonth: string
  expYear: string
  bankName: string
  nickname: string | null
  accountType: UiBankAccountType
  achAccountType: AchAccountType | null
  achAccountHolderType: AchAccountHolderType | null
  accountLast4: string
  routingLast4: string
  billingName: string
  billingZip: string
  isDefault: boolean
  disabled: boolean
}

export function createEmptyMethodForm(
  type: UiPaymentMethodType = UiPaymentMethodType.Card,
): MethodFormState {
  return {
    type,
    firstName: "",
    lastName: "",
    company: "",
    address1: "",
    address2: "",
    city: "",
    stateId: null,
    postalCode: "",
    countryId: null,
    phone: "",
    fax: "",
    brand: "Visa",
    last4: "",
    expMonth: "",
    expYear: "",
    bankName: "",
    accountType: "checking",
    achAccountType: null,
    achAccountHolderType: null,
    nickname: "",
    accountLast4: "",
    routingLast4: "",
    billingName: "",
    billingZip: "",
    isDefault: true,
    disabled: false,
  };
}

export function getCollectMethodFormErrors(
  form: MethodFormState,
  hasAvailableStates: boolean,
): BillingAddressValidationErrors {
  const errors = validateBillingAddressFields(
    {
      firstName: form.firstName,
      lastName: form.lastName,
      postalCode: form.postalCode,
      countryId: form.countryId,
      stateId: form.stateId,
    },
    hasAvailableStates
  )

  if (form.type === UiPaymentMethodType.Ach && form.achAccountType === null) {
    errors.achAccountType = "Account type is required."
  }

  if (form.type === UiPaymentMethodType.Ach && form.achAccountHolderType === null) {
    errors.achAccountHolderType = "Account holder type is required."
  }

  return errors
}

export function isCollectMethodFormValid(
  form: MethodFormState,
  hasAvailableStates: boolean,
) {
  return (
    Object.keys(getCollectMethodFormErrors(form, hasAvailableStates)).length ===
    0
  );
}

export function toMethodFormState(method: UiPaymentMethod): MethodFormState {
  const [firstName, ...remainingNames] = method.billingName.split(" ");

  if (method.type === UiPaymentMethodType.Card) {
    return {
      type: UiPaymentMethodType.Card,
      firstName: firstName ?? "",
      lastName: remainingNames.join(" "),
      company: "",
      address1: "",
      address2: "",
      city: "",
      stateId: null,
      postalCode: method.billingZip,
      countryId: null,
      phone: "",
      fax: "",
      brand: method.brand,
      last4: method.last4,
      expMonth: method.expMonth,
      expYear: method.expYear,
      nickname: "",
      bankName: "",
      accountType: "checking",
      achAccountType: null,
      achAccountHolderType: null,
      accountLast4: "",
      routingLast4: "",
      billingName: method.billingName,
      billingZip: method.billingZip,
      isDefault: method.isDefault,
      disabled: method.isDisabled,
    };
  }

  return {
    type: UiPaymentMethodType.Ach,
    firstName: firstName ?? "",
    lastName: remainingNames.join(" "),
    company: "",
    address1: "",
    address2: "",
    city: "",
    stateId: null,
    postalCode: method.billingZip,
    countryId: null,
    phone: "",
    fax: "",
    brand: "Visa",
    last4: "",
    expMonth: "",
    expYear: "",
    nickname: "",
    bankName: method.bankName,
    accountType: method.accountType,
    achAccountType: null,
    achAccountHolderType: null,
    accountLast4: method.accountLast4,
    routingLast4: method.routingLast4,
    billingName: method.billingName,
    billingZip: method.billingZip,
    isDefault: method.isDefault,
    disabled: method.isDisabled,
  };
}

export function getBillingName(form: MethodFormState) {
  return `${form.firstName} ${form.lastName}`.trim();
}

function toNullableString(value: string | null | undefined) {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function toAddPaymentMethodRequest(
  form: MethodFormState,
  tokenizedPayment: CollectJsTokenizedPayment,
): AddHarnessCustomerPaymentMethodBody {
  return {
    paymentToken: tokenizedPayment.token,
    paymentMethodType: form.type,
    cardType: toNullableString(tokenizedPayment.cardBrand ?? null),
    cardCategory: toNullableString(tokenizedPayment.cardCategory ?? null),
    achAccountType: form.type === UiPaymentMethodType.Ach ? form.achAccountType : null,
    achAccountHolderType: form.type === UiPaymentMethodType.Ach ? form.achAccountHolderType : null,
    paymentMethodNickname: toNullableString(form.nickname),
    billingAddress: {
      firstName: toNullableString(form.firstName),
      lastName: toNullableString(form.lastName),
      company: toNullableString(form.company),
      address1: toNullableString(form.address1),
      address2: toNullableString(form.address2),
      city: toNullableString(form.city),
      stateId: form.stateId,
      postalCode: toNullableString(form.postalCode),
      countryId: form.countryId,
      phone: toNullableString(form.phone),
      fax: toNullableString(form.fax),
    },
    isDefault: form.isDefault,
  };
}

export function toUpdatePaymentMethodRequest(
  currentMethod: UiPaymentMethod,
  form: MethodFormState,
): UpdateHarnessCustomerPaymentMethodBody {
  const request: UpdateHarnessCustomerPaymentMethodBody = {};

  if (currentMethod.isDefault !== form.isDefault) {
    request.isDefaultPaymentMethod = form.isDefault;
  }

  if (currentMethod.isDisabled !== form.disabled) {
    request.disabled = form.disabled;
  }

  return request;
}
